import { ReactNode, useEffect, useRef } from "react";
import { LuCheck } from "react-icons/lu";

export interface Step {
    id: number;
    key: string;
    value: string;
    isActive?: boolean;
    isHidden?: boolean;
    isCompleted: boolean;
    subText?: string | ReactNode;
    showSubText?: boolean;
}

interface Props {
    steps: Step[];
    currentStep: number;
    scrollable?: boolean;
}

const StepperElement = ({
    steps,
    currentStep,
    scrollable = true,
}: Props) => {
    const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        if (stepRefs.current[currentStep]) {
            stepRefs.current[currentStep]?.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "center",
            });
        }
    }, [currentStep]);


    return (
        <div
            className={`relative ${scrollable ? "overflow-hidden" : "overflow-visible"} ${scrollable ? "min-w-full" : "min-w-fit"
                } bg-[#F2F6FF] border border-[#E5E5E5] rounded-lg`}
        >
            <div
                className={`flex ${scrollable ? "overflow-x-auto" : "overflow-visible"} h-full py-2 md:py-3 px-5 md:px-6 max-w-full`}
            >
                {steps
                    ?.filter((step) => !step.isHidden)
                    ?.map((step, index) => {
                        return (
                            <div
                                ref={(el) => {
                                    stepRefs.current[index + 1] = el;
                                }}
                                className="whitespace-pre max-w-[180px] min-w-[210px] flex-1"
                                key={step.key}
                            >
                                <div className="flex items-center">
                                    {step?.isCompleted || index + 1 < currentStep ? (
                                        <div className="rounded-full w-6 md:w-8 h-6 md:h-8 min-w-6 md:min-w-8 min-h-6 md:min-h-8 bg-[#87D5AE] flex items-center justify-center text-white">
                                            <LuCheck className="min-w-[18px] min-h-[18px] md:min-w-[24px] md:min-h-[24px]" />
                                        </div>
                                    ) : (
                                        <div
                                            className={`rounded-full w-6 md:w-8 h-6 md:h-8 min-w-6 md:min-w-8 min-h-6 md:min-h-8 flex items-center justify-center font-sans font-semibold text-sm md:text-base ${step.isActive || index + 1 === currentStep
                                                ? "border border-[#7777E7] bg-[#7777E7] text-white"
                                                : "border border-[#848081] bg-white text-gray-500"
                                                }`}
                                        >
                                            {step.value}
                                        </div>
                                    )}
                                    {index <
                                        steps?.filter((step) => !step.isHidden).length - 1 && (
                                            <div
                                                className={`w-full h-[1.5px] ${index + 1 < currentStep ? "bg-[#6FCD9E]" : "bg-[#E5E5E5]"}`}
                                            />
                                        )}
                                </div>
                                <div className="mt-1">
                                    <p
                                        className={`font-inter font-semibold ${step.isActive ||
                                            (index + 1 === currentStep && !step.isCompleted)
                                            ? "text-[#7777E7]"
                                            : "text-[#544D4F]"
                                            }`}
                                    >
                                        {step.key}
                                    </p>
                                    {step.showSubText && (
                                        <p className="text-xs text-[#848081]">{step.subText}</p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
            </div>
        </div>
    );
};

export default StepperElement;
